import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from "recharts";
import { TrendingUp, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

export default function ProgressLineChart({ childId, compact = false }) {
  const [points, setPoints] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/evaluations?child_id=${childId}`)
      .then((r) => {
        if (!alive) return;
        const data = (r.data || [])
          .slice()
          .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
          .map((e) => ({ date: e.date, percent: e.overall_percent }));
        setPoints(data);
      })
      .catch(() => alive && setPoints([]));
    return () => { alive = false; };
  }, [childId]);

  if (points === null) return null;
  if (points.length === 0) {
    return compact ? null : (
      <div className="text-center text-slate-400 text-xs py-3">لا توجد تقييمات بعد</div>
    );
  }

  const latest = points[points.length - 1].percent;
  const first = points[0].percent;
  const delta = latest - first;
  const up = delta > 0;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-3"
      data-testid={`progress-chart-${childId}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5 text-sky-600" />
          <span className="text-[11px] font-black text-slate-700 uppercase tracking-wider">
            مسار التقدّم
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-black text-sky-700">{latest}%</span>
          {points.length > 1 && (
            <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              delta === 0 ? "text-slate-500 bg-slate-100" : up ? "text-emerald-700 bg-emerald-100" : "text-rose-700 bg-rose-100"
            }`}>
              {delta === 0 ? <Minus className="w-2.5 h-2.5" /> : up ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
              {up ? "+" : ""}{delta}%
            </span>
          )}
        </div>
      </div>
      <div className="h-24" dir="ltr">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 12, right: 8, left: 8, bottom: 4 }}>
            <defs>
              <linearGradient id={`grad-${childId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0284C7" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#0284C7" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 9 }} axisLine={false} tickLine={false}
                   tickFormatter={(d) => d?.slice(5) || ""} interval="preserveStartEnd" />
            <YAxis domain={[0, 100]} hide />
            <Tooltip
              contentStyle={{ borderRadius: 10, borderColor: "#E2E8F0", fontSize: 12, padding: "6px 10px" }}
              formatter={(v) => [`${v}%`, "الإجمالي"]}
              labelFormatter={(d) => `تاريخ: ${d}`}
            />
            <Area type="monotone" dataKey="percent" stroke="none" fill={`url(#grad-${childId})`} />
            <Line type="monotone" dataKey="percent" stroke="#0284C7" strokeWidth={2.5} dot={{ r: 3.5, fill: "#0284C7", stroke: "white", strokeWidth: 2 }} activeDot={{ r: 5 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
